import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { createApiCall } from 'services';

/**
 * One row of the CROSS-certificate activity feed (VERA-315).
 *
 * Deliberately a different shape from `CertificateActivityLog`, which the
 * per-certificate timeline uses. That one carries an actor and no certificate,
 * because the screen already knows which certificate you are looking at. This
 * one inverts it: the certificate IS the thing a row has to identify, and the
 * actor is almost always the reader, so the API does not join it.
 */
export interface ActivityFeedItem {
  id: string;
  type: string;
  created_at: string;
  /**
   * Certificate status this event left the certificate in.
   *
   * OFTEN ABSENT — only some of the paths that write action logs record a status
   * (backfill tracked as VRG-196). Typed as a plain string because it is a wire
   * value: an unrecognised status must degrade to "no chip", not a type error.
   */
  certificate_status?: string | null;
  certificate: {
    id: string;
    /** Product title. Null when the certificate's product row is missing. */
    name: string | null;
    image: string | null;
  };
}

export interface ActivityFeedResponse {
  data: ActivityFeedItem[];
  total: number;
  limit: number;
  offset: number;
}

interface UseActivityFeedParams {
  limit?: number;
  offset?: number;
  enabled?: boolean;
  /**
   * Restrict to these raw action-log types. SERVER-side — the API intersects
   * this with the feed's allowed set, so it can only narrow.
   *
   * Server-side and not client-side, unlike the per-certificate timeline: this
   * feed pages through hundreds of events, so filtering the loaded window would
   * report "3 transfers" to someone who has fifty.
   */
  types?: string[];
  /**
   * Free-text search over the certificate's product title and SKU. SERVER-side,
   * for the same reason `types` is.
   *
   * Filtering the loaded window instead would search only what has been fetched
   * so far, so a match on page three would be invisible while page-one matches
   * showed — and `total`, which the screen prints above the list, comes from the
   * server, so the count would contradict the rows beneath it.
   */
  q?: string;
}

const getActivityFeed = async (
  params: UseActivityFeedParams
): Promise<ActivityFeedResponse> => {
  const response = await createApiCall({
    method: 'GET',
    url: '/certificates/customer/activity',
    params: {
      limit: params.limit || 20,
      offset: params.offset || 0,
      // Comma-separated: the API accepts that or a repeated param. Omitted
      // entirely when empty — an absent filter and an empty one mean different
      // things to the endpoint (see its resolveTypes).
      ...(params.types?.length ? {types: params.types.join(',')} : {}),
      // Omitted entirely when blank, not sent as `q=`. The endpoint trims to
      // `undefined` anyway, but not sending it keeps the request URL — and so
      // the network tab during a walkthrough — honest about whether a search is
      // actually active.
      ...(params.q?.trim() ? {q: params.q.trim()} : {}),
    },
  });

  return response.data as ActivityFeedResponse;
};

/**
 * ONE definition of this query's key and fetcher — same construction rule as
 * `certificateActivityQuery`, so a future prefetch cannot land on a different
 * key than the screen reads (a cache miss just looks like a slow screen, which
 * is why that class of bug survives unreported).
 *
 * The key is the WHOLE params object, which is what makes `q` (and `types`, and
 * `limit`) part of it automatically. That is deliberate and worth keeping: a
 * request parameter absent from the key produces a cache HIT carrying another
 * query's rows — the search box changes and the list does not, or worse, shows
 * a previous term's results as though they matched. Listing keys by hand is how
 * a new parameter gets forgotten; spreading the object cannot forget one.
 */
export const activityFeedQuery = (params: UseActivityFeedParams) => ({
  queryKey: ['activity-feed', params] as const,
  queryFn: () => getActivityFeed(params),
});

export const useActivityFeed = (
  params: UseActivityFeedParams = {}
): UseQueryResult<ActivityFeedResponse, Error> =>
  useQuery({
    ...activityFeedQuery(params),
    enabled: params.enabled !== false,
  });
